'use client';

import AuthEmailInput from '@/components/AuthEmailInput';
import AuthPhoneInput from '@/components/AuthPhoneInput';
import { DEFAULT_OTP_RESET_COUNTDOWN, OTPForm } from '@/components/OTP';
import { PHONE_REGEX } from '@/utils/regex';
import { Button as ButtonChakra } from '@chakra-ui/react';
import { useToasts } from '@medsimples/design-system';
import {
  Box,
  FormControl,
  FormLabel,
  Text,
} from '@medsimples/design-system/src/chakra';
import { Button } from '@medsimples/design-system/src/components';
import {
  type Error as APIError,
  ErrorCode,
  SendEmailOtpRequest,
  SendSmsOtpRequest,
} from '@medsimples/doctor-onboarding-openapi-v2';
import { capitalize } from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import useCountDown from 'react-countdown-hook';
import { doctorOnboardingAPIV2 } from '../../../api';
import BackButton from '../../../components/BackButton';
import { SigninMethodLabel } from '../../../utils/constants';
import { useDesignTokens } from '../../providers/design_tokens_provider';
import { handleOTPError } from '../auth';

export default function Signin() {
  const tokens = useDesignTokens();
  const searchParams = useSearchParams();
  const { errorToast } = useToasts();
  const router = useRouter();
  const [timeLeft, { start, reset }] = useCountDown(
    DEFAULT_OTP_RESET_COUNTDOWN,
  );
  const [value, setValue] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [_isClient, setIsClient] = useState(false);
  const [isWaitingOTP, setIsWaitingOTP] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  const method = useRef(searchParams.get('method'));

  // FIXME: Temporary Next.js Hydration error supression
  useEffect(() => {
    setIsClient(true);
  }, []);

  if (isWaitingOTP) {
    return (
      <OTPForm
        onClickResend={sendLogin}
        onClickVerify={onClickVerify}
        timeLeft={timeLeft}
        onBackButtonAction={onBackButton}
      />
    );
  }

  return (
    <>
      <Box marginBottom={12}>
        <Text textAlign='center' fontSize='xl'>
          Insira seu {SigninMethodLabel[method.current]} abaixo
        </Text>
      </Box>

      <form
        style={{ display: 'contents' }}
        onSubmit={onSendLogin}
        noValidate={true}
      >
        <Box marginBottom={12} w='100%'>
          <FormControl isRequired>
            <FormLabel>
              <Text
                fontSize='0.8em'
                color={tokens.text.secondary.color}
                fontWeight='bold'
                display='inline-block'
              >
                {capitalize(SigninMethodLabel[method.current])}
              </Text>
            </FormLabel>
            {isEmailMethod() ? (
              <AuthEmailInput
                onChange={(e) => setValue(e.target.value)}
                value={value}
              />
            ) : (
              <AuthPhoneInput
                onChange={(e) => setValue(e.target.value)}
                value={value}
              />
            )}
          </FormControl>
        </Box>

        <Box w='100%'>
          {showSignup && (
            <ButtonChakra
              variant='link'
              marginBottom={5}
              colorScheme={'blue'}
              fontSize='md'
              size={'lg'}
              w={'100%'}
              onClick={() => {
                setLoading(true);
                router.push('/signup');
              }}
            >
              Novo Cadastro
            </ButtonChakra>
          )}
          <Button
            data-testid='button'
            isLoading={isLoading}
            type='submit'
            colorScheme={tokens.button.primary.scheme}
          >
            Continuar
          </Button>
        </Box>
        <BackButton
          router={router}
          action={() => router.back()}
          marginTop={5}
        />
      </form>
    </>
  );

  function isEmailMethod() {
    return method.current === 'email';
  }

  function onBackButton() {
    if (isWaitingOTP) {
      setIsWaitingOTP(false);
      return;
    }

    router.back();
    router.refresh();
  }

  async function sendLogin() {
    setLoading(true);

    // todo: use enum
    const payload = isEmailMethod()
      ? {
          type: SendEmailOtpRequest.type.EMAIL_OTP,
          email: value,
        }
      : {
          type: SendSmsOtpRequest.type.SMS_OTP,
          phone: value.replace(PHONE_REGEX, ''),
        };

    await doctorOnboardingAPIV2()
      .user.sendLogin(payload)
      .then((res) => {
        if (!res.success) {
          throw 'Ocorreu um erro inesperado!';
        }

        setIsWaitingOTP(true);
        start();
      })
      .catch((err) => {
        console.error(err);
        const apiError: APIError = err?.body?.error;
        if (apiError?.code === ErrorCode.UserNotFound) {
          errorToast(
            'Inicie um novo cadastro acima',
            `${isEmailMethod() ? 'Email' : 'Telefone'} não cadastrado`,
          );
          setShowSignup(true);
        }
      })
      .finally(() => setLoading(false));
  }

  async function onSendLogin(e: FormEvent) {
    e.preventDefault();
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const form: any = e.target;
    if (!form.checkValidity()) {
      errorToast(
        'Atenção',
        'Verifique se todos os campos foram preenchidos corretamente',
      );
      return;
    }
    await sendLogin();
  }

  async function onClickVerify(code: string) {
    if (!code || code.length < 6) {
      errorToast('Erro: insira o código de verificação corretamente');
      return;
    }

    // todo: use enum
    const payload = isEmailMethod()
      ? {
          type: SendEmailOtpRequest.type.EMAIL_OTP,
          email: value,
        }
      : {
          type: SendSmsOtpRequest.type.SMS_OTP,
          phone: value.replace(PHONE_REGEX, ''),
        };

    setLoading(true);
    await doctorOnboardingAPIV2()
      .user.login({
        code,
        ...payload,
      })
      .then((res) => {
        if (!res?.success) {
          throw 'Ocorreu um erro inesperado!';
        }

        router.replace('/check_info');
      })
      .catch((err) => {
        console.error(err);
        const apiError: APIError = err?.body.error;

        if (apiError?.code && apiError.code === ErrorCode.UserProviderWrong) {
          errorToast(
            'Você já possui um cadastro em outro sistema, você será redirecionado em 5 segundos!',
            'Cadastro existente',
          );
          setTimeout(
            () => window.location.assign(`https://${apiError.message}`),
            5000,
          );
          return;
        }

        const [title, msg] = handleOTPError(err);
        errorToast(msg, title);
      })
      .finally(() => setLoading(false));
  }
}
